// An agent's knowledge base (DOCS-KB §2 "Knowledge tab"): the docs, spaces and page kinds a DE
// (its profile's `?tab=knowledge`) or a personal crew (My agent's "Knowledge" section) reads when
// it works, re-read on every run. The list is base plan: with the `agents` add-on off it shows
// read-only under a Notice; adding (`KnowledgePickerDialog`) and removing need the add-on and
// `can_edit` (the agent's supervisor, a crew's owner, admins).
import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  buttonVariants,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ListRow,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SearchField,
  Segmented,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BookOpenIcon,
  FileTextIcon,
  FolderOpenIcon,
  LayersIcon,
  ListChecksIcon,
  PlusIcon,
  ScaleIcon,
  XIcon,
} from "lucide-react";
import { type ComponentType, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { useFeature } from "../use-feature";
import { pagesQuery, spacesQuery } from "../work/work-queries";
import { invalidateKnowledge, knowledgeQuery } from "./agent-queries";

type Knowledge = OperationOutput<"agentKnowledge.list">;
type Source = Knowledge["data"][number];
type PageSummary = OperationOutput<"pages.list">["data"][number];
type SourceInput =
  | { kind: "page"; page_id: string }
  | { kind: "space"; space_id: string }
  | {
      kind: "kind";
      page_kind: PickKind;
    };
type PickKind = "sop" | "policy" | "kb";
type Chip = "all" | PickKind | "page";
type Icon = ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" }>;

const KIND_ICONS: Record<string, Icon> = {
  page: FileTextIcon,
  sop: ListChecksIcon,
  policy: ScaleIcon,
  kb: BookOpenIcon,
};
const KIND_PLURALS: Record<string, string> = {
  page: "pages",
  sop: "SOPs",
  policy: "policies",
  kb: "KB articles",
  vision: "visions",
  journal: "journals",
  decision: "decisions",
  strategy: "strategies",
};
const KIND_SINGULAR: Record<PickKind, string> = { sop: "SOP", policy: "policy", kb: "KB article" };
const CHIPS: { value: Chip; label: string }[] = [
  { value: "all", label: "All" },
  { value: "sop", label: "SOPs" },
  { value: "policy", label: "Policies" },
  { value: "kb", label: "Knowledge base" },
  { value: "page", label: "Pages" },
];
const PICK_KINDS: readonly string[] = ["sop", "policy", "kb"];

/** "just now", "5m ago", "2h ago", "3d ago"; "never" for null. */
export function lastReadText(iso: string | null, now = Date.now()): string {
  if (!iso) return "never";
  const min = Math.floor(Math.max(0, now - Date.parse(iso)) / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const docs = (n: number) => `${n} ${n === 1 ? "doc" : "docs"}`;

/** The target key of a source or a picked row: one per page, space or kind. */
const keyOf = (s: SourceInput) =>
  s.kind === "page"
    ? `page:${s.page_id}`
    : s.kind === "space"
      ? `space:${s.space_id}`
      : `kind:${s.page_kind}`;
const sourceKey = (s: Source) =>
  s.source_kind === "page"
    ? `page:${s.page_id}`
    : s.source_kind === "space"
      ? `space:${s.space_id}`
      : `kind:${s.page_kind}`;

export function KnowledgeTab({
  api,
  company,
  membershipId,
  agentName,
}: {
  api: Api;
  company: CompanyAccess;
  membershipId: string;
  agentName: string;
}) {
  const co = company.id;
  const agents = useFeature("agents");
  const qc = useQueryClient();
  const knowledge = useQuery(knowledgeQuery(api, co, membershipId));
  const [picking, setPicking] = useState(false);
  const remove = useMutation({
    mutationFn: (sourceId: string) =>
      api.client.agentKnowledge.remove({
        params: { co, membership_id: membershipId, source_id: sourceId },
      }),
    onSettled: () => invalidateKnowledge(qc, co),
  });

  if (knowledge.isPending) return <Loading label="Loading knowledge" rows={3} />;
  if (knowledge.isError) return <LoadError what="Knowledge" error={knowledge.error} />;
  const k = knowledge.data;
  const writable = k.can_edit && agents.enabled && !agents.readOnly;

  return (
    <div className="flex min-w-0 flex-col gap-3" data-slot="knowledge">
      {agents.enabled ? null : (
        <Notice
          tone="info"
          action={
            <Link
              to="/c/$slug/setup/addons"
              params={{ slug: company.slug }}
              hash="agents"
              className={buttonVariants({ variant: "secondary", size: "sm" })}
            >
              Add-ons
            </Link>
          }
        >
          Turn on Agents to use the knowledge base
        </Notice>
      )}
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {agentName} reads these when it works. Last read {lastReadText(k.last_read_at)}.
        </p>
        {writable ? (
          <Button size="sm" onClick={() => setPicking(true)}>
            <PlusIcon aria-hidden="true" />
            Add docs
          </Button>
        ) : null}
      </div>
      {k.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing yet. Add docs to give {agentName} something to read.
        </p>
      ) : (
        <div className="min-w-0 border-t border-subtle" data-slot="knowledge-sources">
          {k.data.map((s) => (
            <SourceRow
              key={s.id}
              slug={company.slug}
              source={s}
              onRemove={writable ? () => remove.mutate(s.id) : undefined}
            />
          ))}
        </div>
      )}
      {k.can_edit ? null : (
        <p className="text-xs text-muted-foreground">
          Only {agentName}'s supervisor and admins change this.
        </p>
      )}
      {writable ? (
        <KnowledgePickerDialog
          api={api}
          company={company}
          membershipId={membershipId}
          existing={k.data}
          open={picking}
          onOpenChange={setPicking}
        />
      ) : null}
    </div>
  );
}

function SourceRow({
  slug,
  source: s,
  onRemove,
}: {
  slug: string;
  source: Source;
  onRemove: (() => void) | undefined;
}) {
  const Lead =
    s.source_kind === "space"
      ? FolderOpenIcon
      : s.source_kind === "kind"
        ? LayersIcon
        : (KIND_ICONS[s.page?.kind ?? "page"] ?? FileTextIcon);
  const title =
    s.source_kind === "space"
      ? `Space: ${s.space?.name ?? "Removed space"} · ${docs(s.page_count)}`
      : s.source_kind === "kind"
        ? `All ${KIND_PLURALS[s.page_kind ?? "page"] ?? s.page_kind} · ${docs(s.page_count)}`
        : (s.page?.title ?? "Removed page");
  const name =
    s.source_kind === "space"
      ? s.space?.name
      : s.source_kind === "kind"
        ? `All ${KIND_PLURALS[s.page_kind ?? "page"] ?? s.page_kind}`
        : s.page?.title;
  return (
    <ListRow
      data-source-kind={s.source_kind}
      lead={<Lead aria-hidden={true} />}
      title={title}
      {...(s.source_kind === "page" && s.page
        ? {
            render: <Link to="/c/$slug/work/p/$pageId" params={{ slug, pageId: s.page.id }} />,
            meta: s.page.space_name,
          }
        : {})}
      trailing={
        <span className="w-20 text-right" title="Last read">
          {lastReadText(s.last_read_at)}
        </span>
      }
      actions={
        onRemove ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove ${name ?? "source"}`}
                  onClick={onRemove}
                />
              }
            >
              <XIcon aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent>Remove</TooltipContent>
          </Tooltip>
        ) : undefined
      }
    />
  );
}

const inChip = (p: PageSummary, chip: Chip) =>
  chip === "all" ? true : chip === "page" ? !PICK_KINDS.includes(p.kind) : p.kind === chip;

/** "Add docs": the company's docs with the Docs filters; each row, a space or a kind, ticked. */
export function KnowledgePickerDialog({
  api,
  company,
  membershipId,
  existing,
  open,
  onOpenChange,
}: {
  api: Api;
  company: CompanyAccess;
  membershipId: string;
  existing: Source[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const pages = useQuery({ ...pagesQuery(api, co), enabled: open });
  const spaces = useQuery({ ...spacesQuery(api, co), enabled: open });
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<Chip>("all");
  const [spaceId, setSpaceId] = useState("");
  const [picked, setPicked] = useState<Map<string, SourceInput>>(new Map());
  const had = useMemo(() => new Set(existing.map(sourceKey)), [existing]);

  const reset = () => {
    setQ("");
    setChip("all");
    setSpaceId("");
    setPicked(new Map());
  };
  const add = useMutation({
    mutationFn: (sources: SourceInput[]) =>
      api.client.agentKnowledge.add({
        params: { co, membership_id: membershipId },
        body: { sources },
      }),
    onSuccess: async () => {
      await invalidateKnowledge(qc, co);
      reset();
      onOpenChange(false);
    },
  });

  const all = (pages.data?.data ?? []).filter((p) => !p.archived_at);
  const spaceName = (id: string) => spaces.data?.data.find((s) => s.id === id)?.name ?? "";
  const needle = q.trim().toLowerCase();
  const rows = all.filter(
    (p) =>
      inChip(p, chip) &&
      (!spaceId || p.space_id === spaceId) &&
      (!needle || p.title.toLowerCase().includes(needle)),
  );
  const spaceCount = spaceId ? all.filter((p) => p.space_id === spaceId).length : 0;
  const kindCount =
    chip !== "all" && chip !== "page" ? all.filter((p) => p.kind === chip).length : 0;

  const toggle = (s: SourceInput, on: boolean) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (on) next.set(keyOf(s), s);
      else next.delete(keyOf(s));
      return next;
    });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add docs</DialogTitle>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <SearchField
              aria-label="Filter docs by title"
              placeholder="Filter by title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <NativeSelect
              size="sm"
              aria-label="Space"
              value={spaceId}
              onChange={(e) => setSpaceId(e.target.value)}
            >
              <NativeSelectOption value="">All spaces</NativeSelectOption>
              {(spaces.data?.data ?? []).map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
          <Segmented
            size="sm"
            aria-label="Kind"
            options={CHIPS}
            value={chip}
            onValueChange={setChip}
          />
          <div
            className="max-h-[50dvh] min-w-0 overflow-y-auto border-t border-subtle"
            data-slot="knowledge-picker"
          >
            {spaceId ? (
              <PickRow
                icon={FolderOpenIcon}
                label={`Add the whole space (${docs(spaceCount)})`}
                source={{ kind: "space", space_id: spaceId }}
                had={had}
                picked={picked}
                onToggle={toggle}
              />
            ) : null}
            {chip !== "all" && chip !== "page" ? (
              <PickRow
                icon={LayersIcon}
                label={`Add every ${KIND_SINGULAR[chip]} (${kindCount})`}
                source={{ kind: "kind", page_kind: chip }}
                had={had}
                picked={picked}
                onToggle={toggle}
              />
            ) : null}
            {pages.isPending && open ? (
              <Loading label="Loading docs" rows={3} />
            ) : rows.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">No docs match.</p>
            ) : (
              rows.map((p) => (
                <PickRow
                  key={p.id}
                  icon={KIND_ICONS[p.kind] ?? FileTextIcon}
                  label={p.title}
                  meta={spaceName(p.space_id)}
                  source={{ kind: "page", page_id: p.id }}
                  had={had}
                  picked={picked}
                  onToggle={toggle}
                />
              ))
            )}
          </div>
        </div>
        <DialogFooter className="items-center sm:justify-between">
          <span className="text-sm text-muted-foreground tabular-nums">{picked.size} selected</span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={picked.size === 0 || add.isPending}
              onClick={() => add.mutate([...picked.values()])}
            >
              Add
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PickRow({
  icon: Icon,
  label,
  meta,
  source,
  had,
  picked,
  onToggle,
}: {
  icon: Icon;
  label: string;
  meta?: string;
  source: SourceInput;
  had: Set<string>;
  picked: Map<string, SourceInput>;
  onToggle: (s: SourceInput, on: boolean) => void;
}) {
  const key = keyOf(source);
  const already = had.has(key);
  const on = already || picked.has(key);
  return (
    <div
      data-pick={key}
      className="flex h-9 min-w-0 items-center gap-3 border-b border-subtle px-3 hover:bg-sunken pointer-coarse:h-11"
    >
      <Checkbox
        aria-label={label}
        checked={on}
        disabled={already}
        onCheckedChange={(next) => onToggle(source, next === true)}
      />
      <Icon aria-hidden={true} className="size-4 shrink-0 text-faint" />
      {/* The title toggles too (a pointer convenience; the checkbox is the control). */}
      <span
        aria-hidden="true"
        className="min-w-0 flex-1 cursor-pointer truncate text-sm"
        onClick={() => {
          if (!already) onToggle(source, !on);
        }}
      >
        {label}
      </span>
      {meta ? <span className="shrink-0 text-xs text-muted-foreground">{meta}</span> : null}
    </div>
  );
}
